import { React, useEffect, useState } from "react";
import '../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import "../styles.css";
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { useSubscription } from "../hooks/useSubscription";
import { usePlanState } from "../hooks/usePlanState";
import { effectivePlanLabel } from "../utils/tier";
import { useFormBuilder } from "../hooks/useFormBuilder";
import dayjs from 'dayjs';
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
import { Helmet } from "react-helmet";
import { useLogout } from "../hooks/useLogout";

const SUGGESTED_PROMPTS = [
  "Customer feedback survey",
  "Job application form",
  "Event registration",
  "Lead capture form",
  "Product waitlist signup",
  "Contact us form",
];

function StatCard({ label, value, icon, accent }) {
  return (
    <div className="bg-white dark:bg-[#23263a] rounded-2xl border border-gray-200 dark:border-gray-700/60 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 font-Rajdhani">{label}</span>
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg ${accent}`}><i className={icon}></i></span>
      </div>
      <p className="mt-3 text-2xl font-bold text-gray-900 dark:text-white font-display">{value}</p>
    </div>
  );
}

function Dashboard() {
  const { user, dispatch } = useAuthContext()
  const { logout } = useLogout();
  const { subsInfo } = useSubscription()
  const { effectivePlan, planState } = usePlanState()
  const { getAllForms } = useFormBuilder()
  const navigate = useNavigate()

  const [forms, setForms] = useState([])
  const [prompt, setPrompt] = useState("")

  useEffect(() => {
    const users = Cookies.get('token')
    if (!users) { logout() }
    try {
      const User = jwtDecode(users)
      const checkToken = async () => {
        const isExpired = dayjs.unix(User.exp).diff(dayjs()) < 1;
        if (!isExpired) {
          const data = await getAllForms()
          if (data) setForms(data)
        } else {
          const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/user/refresh-token`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          })
          if (response.status === 200) {
            const json = await response.json()
            dispatch({ type: 'LOGIN', payload: json.token })
            navigate("/login")
          } else {
            logout()
          }
        }
      }
      checkToken()
    } catch (e) {
      if (e.message.includes("Invalid token specified")) { logout() }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  if (!user) {
    return navigate("/")
  }

  const formPlan = effectivePlan
  const isPaidForms = formPlan !== "free"
  // LTD users must see their actual Lifetime tier, not the SaaS slug the tier maps to
  // internally (Tier 2 → "pro"), which overstates what they get. See utils/tier.js.
  const planLabel = effectivePlanLabel(planState) || (formPlan.charAt(0).toUpperCase() + formPlan.slice(1))
  const totalResponses = forms.reduce((a, f) => a + (f.responseCount || 0), 0)
  const publishedCount = forms.filter((f) => f.isPublished).length
  const formsLimit = subsInfo?.details?.totalforms
  // 999999 (and 0/undefined) is the "unlimited" sentinel — show it as unlimited, not a raw number.
  const unlimitedForms = !formsLimit || formsLimit >= 999999
  const recentForms = [...forms].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)).slice(0, 4)

  const startWithPrompt = (text) => {
    navigate(`/form-builder/create${text ? `?prompt=${encodeURIComponent(text)}` : ""}`)
  }

  return (
    <div className="min-h-screen max-w-6xl mx-auto px-4 sm:px-6 py-8 text-gray-900 dark:text-white">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Dashboard - Content Flash AI</title>
        <meta name="description" content="Build AI-powered forms in seconds with Content Flash AI." />
      </Helmet>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold font-Rajdhani">Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Build, share, and analyze AI-powered forms.</p>
        </div>
        {isPaidForms ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
            {planLabel} plan 💎
          </span>
        ) : (
          <Link to="/settings/plans" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition no-underline shadow-sm shadow-indigo-600/30">
            Upgrade plan
          </Link>
        )}
      </div>

      {/* AI form creation hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-600 to-violet-600 text-white p-6 sm:p-8 mb-8 shadow-lg shadow-indigo-600/20">
        <div className="absolute -top-12 -right-12 w-56 h-56 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-16 -left-10 w-64 h-64 bg-violet-400/20 rounded-full blur-3xl" />
        <div className="relative max-w-2xl">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/15 backdrop-blur"><i className="bi bi-stars" /> AI Form Builder</span>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold font-Rajdhani">What form do you want to build?</h2>
          <p className="mt-1.5 text-indigo-100">Describe it in a sentence — AI generates the fields, logic and theme instantly.</p>

          <form
            onSubmit={(e) => { e.preventDefault(); startWithPrompt(prompt.trim()); }}
            className="mt-5 flex flex-col sm:flex-row gap-2"
          >
            <input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. A customer feedback form with a 1–5 rating"
              className="flex-1 px-4 py-3 rounded-xl text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/60"
            />
            <button type="submit" className="px-5 py-3 rounded-xl bg-white text-indigo-700 font-semibold text-sm hover:bg-indigo-50 transition whitespace-nowrap">
              Generate with AI →
            </button>
          </form>

          <div className="mt-4 flex flex-wrap gap-2">
            {SUGGESTED_PROMPTS.map((p) => (
              <button key={p} onClick={() => startWithPrompt(p)} className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur transition">
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Form stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total forms" value={unlimitedForms ? `${forms.length}` : `${forms.length} / ${formsLimit}`} icon="bi bi-ui-checks-grid" accent="bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300" />
        <StatCard label="Total responses" value={totalResponses.toLocaleString()} icon="bi bi-inbox" accent="bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300" />
        <StatCard label="Published" value={publishedCount} icon="bi bi-broadcast" accent="bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300" />
        <StatCard label="Plan" value={planLabel} icon="bi bi-gem" accent="bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300" />
      </div>

      {/* Your forms */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold font-Rajdhani">Your forms</h2>
          <Link to="/form-builder" className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 no-underline">View all →</Link>
        </div>

        {forms.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-[#23263a] p-10 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 flex items-center justify-center text-2xl">
              <i className="bi bi-magic" />
            </div>
            <p className="text-lg font-semibold">No forms yet</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">Describe a form above, or start from scratch.</p>
            <button onClick={() => startWithPrompt("")} className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition">
              Create your first form
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {recentForms.map((f) => (
              <div key={f.formId} className="bg-white dark:bg-[#23263a] rounded-2xl border border-gray-200 dark:border-gray-700/60 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="h-1.5" style={{ backgroundColor: f.theme?.primaryColor || "#6366f1" }} />
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">{f.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium ${f.isPublished ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"}`}>
                      {f.isPublished ? "Published" : "Draft"}
                    </span>
                  </div>
                  <div className="flex gap-4 text-sm text-gray-500 dark:text-gray-400 mt-2">
                    <span><span className="font-semibold text-gray-700 dark:text-gray-300">{f.responseCount || 0}</span> responses</span>
                    <span>{f.fields?.length || 0} fields</span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => navigate(`/form-builder/edit/${f.formId}`)} className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-[#2c2f44] transition">Edit</button>
                    <button onClick={() => navigate(`/form-builder/${f.formId}/responses`)} className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-[#2c2f44] transition">Responses</button>
                    <button onClick={() => navigate(`/form-builder/${f.formId}/insights`)} className="px-3 py-1.5 text-sm font-medium rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition">Insights</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* More tools (legacy content suite) */}
      <div>
        <h2 className="text-lg font-bold font-Rajdhani mb-4">More AI tools</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { to: "/flashes", icon: "bi bi-lightning-charge", title: "Content Flashes", desc: "67+ templates for blogs, emails & ads", accent: "bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300" },
            { to: "/ai-image-generation", icon: "bi bi-image", title: "AI Images", desc: "Generate visuals with DALL·E", accent: "bg-purple-50 text-purple-600 dark:bg-purple-500/15 dark:text-purple-300" },
            { to: "/text-to-speech", icon: "bi bi-mic", title: "Text to Speech", desc: "Turn text into natural voiceovers", accent: "bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300" },
          ].map((t) => (
            <Link key={t.to} to={t.to} className="no-underline bg-white dark:bg-[#23263a] rounded-2xl border border-gray-200 dark:border-gray-700/60 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${t.accent}`}><i className={t.icon} /></span>
              <h3 className="mt-3 font-semibold text-gray-900 dark:text-white">{t.title}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
